import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, UserRound, Users, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  listWecomUsers,
  wecomUserDisplayName,
  type WecomUser,
} from "@/lib/wecom-users";

type WecomUserMultiSelectProps = {
  accountId: string | number;
  value: string[];
  onChange: (userids: string[]) => void;
  /** 已解析过的成员（含选中项），供父级做姓名映射 */
  onUsersResolved?: (users: WecomUser[]) => void;
  disabled?: boolean;
  className?: string;
  /**
   * form：页面筛选表单（默认）；header：聊天窗顶栏紧凑触发器
   */
  variant?: "form" | "header";
  /** form 变体空值文案；header 变体默认「全部跟进人」 */
  emptyLabel?: string;
};

const SEARCH_DEBOUNCE_MS = 250;
const PICKER_LIMIT = 50;

/** 顶栏/触发器姓名摘要：最多 maxNames 个，超出加「等」 */
function selectedUsersSummary(
  userids: string[],
  selectedUsers: WecomUser[],
  maxNames = 2,
): { text: string; title: string } {
  const names = userids.map((id) => {
    const user = selectedUsers.find((u) => u.userid === id);
    return wecomUserDisplayName(user ?? { userid: id, name: null });
  });
  const full = names.join("、");
  if (names.length <= maxNames) {
    return { text: full, title: full };
  }
  const shown = names.slice(0, maxNames).join("、");
  return { text: `${shown} 等`, title: full };
}

/**
 * 企微跟进人：可搜索、可多选的下拉（Popover + Command）。
 * 列表按关键字走 API，避免一次渲染上千人。
 */
export function WecomUserMultiSelect({
  accountId,
  value,
  onChange,
  onUsersResolved,
  disabled = false,
  className,
  variant = "form",
  emptyLabel,
}: WecomUserMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState<WecomUser[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<WecomUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  /** 避免 value 变更重建 fetchOptions → 重新请求 → loading 闪烁 */
  const valueRef = useRef(value);
  valueRef.current = value;

  const selectedSet = useMemo(() => new Set(value), [value]);
  const isHeader = variant === "header";
  const resolvedEmptyLabel = emptyLabel ?? (isHeader ? "全部跟进人" : "选择跟进人…");

  const emitResolved = useCallback(
    (users: WecomUser[]) => {
      if (!onUsersResolved || users.length === 0) return;
      onUsersResolved(users);
    },
    [onUsersResolved],
  );

  const fetchOptions = useCallback(
    async (q: string) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setLoadError(null);
      try {
        const payload = await listWecomUsers(accountId, {
          q: q.trim() || undefined,
          limit: PICKER_LIMIT,
        });
        if (requestId !== requestIdRef.current) return;
        const rows = payload.wecom_users ?? [];
        setOptions(rows);
        emitResolved(rows);
        const currentValue = valueRef.current;
        // 用搜索结果补全已选成员姓名（顶栏摘要用）
        setSelectedUsers((prev) => {
          const byId = new Map(prev.map((u) => [u.userid, u]));
          for (const row of rows) {
            if (currentValue.includes(row.userid)) byId.set(row.userid, row);
          }
          return currentValue
            .map((id) => byId.get(id))
            .filter((u): u is WecomUser => u != null);
        });
      } catch {
        if (requestId !== requestIdRef.current) return;
        setOptions([]);
        setLoadError("无法加载成员");
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [accountId, emitResolved],
  );

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => {
      void fetchOptions(search);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [open, search, fetchOptions]);

  useEffect(() => {
    setSelectedUsers((prev) => prev.filter((u) => selectedSet.has(u.userid)));
  }, [selectedSet]);

  function toggleUser(user: WecomUser) {
    const checked = selectedSet.has(user.userid);
    if (checked) {
      onChange(value.filter((id) => id !== user.userid));
      setSelectedUsers((prev) => prev.filter((u) => u.userid !== user.userid));
    } else {
      onChange([...value, user.userid]);
      setSelectedUsers((prev) =>
        prev.some((u) => u.userid === user.userid) ? prev : [...prev, user],
      );
      emitResolved([user]);
    }
  }

  function removeUserid(userid: string) {
    onChange(value.filter((id) => id !== userid));
    setSelectedUsers((prev) => prev.filter((u) => u.userid !== userid));
  }

  function clearAll() {
    onChange([]);
    setSelectedUsers([]);
  }

  const formTriggerLabel =
    value.length === 0
      ? resolvedEmptyLabel
      : value.length === 1
        ? wecomUserDisplayName(
            selectedUsers.find((u) => u.userid === value[0]) ?? {
              userid: value[0],
              name: null,
            },
          )
        : `已选 ${value.length} 人`;

  const headerSummary =
    value.length === 0
      ? { text: resolvedEmptyLabel, title: "按跟进人筛选客户列表" }
      : selectedUsersSummary(value, selectedUsers);
  const HeaderIcon = value.length > 1 ? Users : UserRound;

  const picker = (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {isHeader ? (
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-label="按跟进人筛选客户"
            disabled={disabled}
            title={headerSummary.title}
            className={cn(
              "flex min-w-0 max-w-full items-center gap-2.5 rounded-md text-left outline-none transition-colors",
              "hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring",
              "disabled:pointer-events-none disabled:opacity-50",
              open && "bg-muted/60",
            )}
          >
            <span
              className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground"
              aria-hidden
            >
              <HeaderIcon className="size-4" />
            </span>
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">跟进人</p>
              <p
                className={cn(
                  "flex items-center gap-1 truncate text-sm font-medium",
                  value.length === 0 && "font-normal text-muted-foreground",
                )}
              >
                <span className="truncate">{headerSummary.text}</span>
                <ChevronsUpDown className="size-3.5 shrink-0 opacity-50" aria-hidden />
              </p>
            </div>
          </button>
        ) : (
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label="按跟进人筛选"
            disabled={disabled}
            className="h-auto min-h-9 w-full max-w-xs justify-between gap-2 px-3 py-1.5 font-normal"
          >
            {value.length === 0 ? (
              <span className="truncate text-muted-foreground">{resolvedEmptyLabel}</span>
            ) : value.length === 1 ? (
              <span className="min-w-0 flex-1 text-left leading-tight">
                <span className="block truncate text-sm">{formTriggerLabel}</span>
                <span className="block truncate text-[10px] text-muted-foreground">
                  {value[0]}
                </span>
              </span>
            ) : (
              <span className="truncate">{formTriggerLabel}</span>
            )}
            <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(100vw-2rem,18rem)] p-0"
        align={isHeader ? "start" : "start"}
      >
        <Command shouldFilter={false}>
          <div className="flex items-center gap-1 pr-1">
            <div className="min-w-0 flex-1 [&_[data-slot=command-input-wrapper]]:pr-0">
              <CommandInput
                placeholder="搜索姓名或 userid…"
                value={search}
                onValueChange={setSearch}
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-9 shrink-0 px-2 text-xs"
              disabled={value.length === 0}
              aria-label="清除筛选"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                clearAll();
              }}
            >
              清除
            </Button>
          </div>
          <CommandList className="max-h-64">
            {/* 已有列表时保持展示，避免重新搜索时整表被「搜索中」替换闪烁 */}
            {loading && options.length === 0 ? (
              <p className="px-3 py-4 text-sm text-muted-foreground">搜索中…</p>
            ) : null}
            {!loading && loadError ? (
              <p className="px-3 py-4 text-sm text-destructive">{loadError}</p>
            ) : null}
            {!loadError && !(loading && options.length === 0) ? (
              <>
                <CommandEmpty>
                  {search.trim() ? "没有匹配的成员" : "输入关键字搜索成员，或稍候列表加载"}
                </CommandEmpty>
                <CommandGroup>
                  {options.map((user) => {
                    const selected = selectedSet.has(user.userid);
                    const label = wecomUserDisplayName(user);
                    return (
                      <CommandItem
                        key={user.userid}
                        value={`${label} ${user.userid}`}
                        onSelect={() => toggleUser(user)}
                        className="items-start"
                      >
                        <Check
                          className={cn(
                            "mt-0.5 size-4 shrink-0",
                            selected ? "opacity-100" : "opacity-0",
                          )}
                        />
                        <span className="min-w-0 flex-1 leading-tight">
                          <span className="block truncate text-sm">{label}</span>
                          <span className="block truncate text-[10px] text-muted-foreground">
                            {user.userid}
                          </span>
                        </span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );

  if (isHeader) {
    return <div className={cn("min-w-0", className)}>{picker}</div>;
  }

  return (
    <div className={cn("w-full max-w-xs space-y-2", className)}>
      {picker}

      {value.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {value.map((userid) => {
            const user = selectedUsers.find((u) => u.userid === userid);
            const label = wecomUserDisplayName(user ?? { userid, name: null });
            return (
              <li key={userid}>
                <span className="inline-flex max-w-[10rem] items-start gap-1 rounded-md bg-muted px-2 py-1">
                  <span className="min-w-0 flex-1 leading-tight">
                    <span className="block truncate text-xs">{label}</span>
                    <span className="block truncate text-[10px] text-muted-foreground">
                      {userid}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="mt-0.5 shrink-0 rounded-full p-0.5 text-muted-foreground hover:bg-background hover:text-foreground"
                    aria-label={`移除 ${label}`}
                    disabled={disabled}
                    onClick={() => removeUserid(userid)}
                  >
                    <X className="size-3.5" />
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
